import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DropzoneOptions, useDropzone, FileWithPath } from 'react-dropzone';
import { Button, Card, Overlay, OverlayTrigger, ProgressBar, Stack, Table, Tooltip } from 'react-bootstrap';
import { useField, useFormikContext } from 'formik';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { FormField, ErrorElement, OnDownloadFileArgs, OnDownloadFileFn } from '../../../types/general';
import * as FieldsProps from '../../../types/fields';
import { fileToBlob, formatBytes } from '../../../helpers';
import SuccessAlert from '../../../components/Alert/SuccessAlert';
import ErrorAlert from '../../../components/Alert/ErrorAlert';
import Confirm from '../../../components/Alert/Confirm';
import './File.scss';

export type Props = FormField;

interface ReadOnlyProps extends Props {
	files: FileWithContents[];
}

interface FileWithContents extends FileValue {
	preview?: string;
	content?: Blob;
}

interface FileValue {
	uniqueId?: string;
	path: string;
	type: string;
	hash?: string;
	fileName: string;
	fileLength: number;
}

interface FileHashProps {
	hash: string;
}

interface PrepareDownloadPropsArgs extends OnDownloadFileArgs {
	onDownloadFile?: OnDownloadFileFn;
}

function prepareDownloadProps(args: PrepareDownloadPropsArgs) {
	const { field, file, onDownloadFile } = args;
	const propsDownloadFile = onDownloadFile ? onDownloadFile({ field, file }) : undefined;
	if (typeof propsDownloadFile === 'string') {
		return {
			href: propsDownloadFile,
			target: '_blank',
			onClick: (e: React.MouseEvent) => e.stopPropagation(),
		};
	}
	return propsDownloadFile;
}

const FileHash: React.FC<FileHashProps> = (props) => {
	const { hash } = props;
	const [copied, setCopied] = useState(false);
	const copyTarget = useRef(null);

	return (
		<Fragment>
			<div className='hash ms-auto' ref={copyTarget} onClick={(e) => e.stopPropagation()}>
				<CopyToClipboard
					text={hash}
					onCopy={() => {
						setCopied(true);
						setTimeout(() => {
							setCopied(false);
						}, 3000);
					}}
				>
					<div>
						<i className='bi bi-check-lg' /> Impronta
					</div>
				</CopyToClipboard>
			</div>
			<Overlay target={copyTarget.current} show={copied} placement='bottom'>
				{(props) => <Tooltip {...props}>Copiato</Tooltip>}
			</Overlay>
		</Fragment>
	);
};

const FileReadOnly: React.FC<ReadOnlyProps> = (props) => {
	const { schema, files, onDownloadFile } = props;
	const { name } = schema;
	const field = useField<FileValue | FileValue[] | undefined>(name);

	if (!files || files.length === 0) {
		return <div className='empty'>Nessun file caricato</div>;
	}

	const file = files[0];
	const propsDownloadFile = prepareDownloadProps({ field, file, onDownloadFile });
	return (
		<div className='d-grid gap-2 input'>
			<Button variant='primary' {...propsDownloadFile}>
				{file.fileName} ({formatBytes(files.reduce((sum, file) => sum + (file.fileLength || 0), 0))})
			</Button>
			{files && files[0] && files[0].hash && <FileHash hash={files[0].hash} />}
		</div>
	);
};

/**
 * Uploader to send files
 */
const File: React.FC<Props> = (props) => {
	const { schema, options = {}, onSelectFiles, onDownloadFile, onRemoveFile } = props;
	const { name, props: fieldProps } = schema;
	const { fileMaxSize, typesOfFile } = options;
	const {
		readonly,
		accept,
		multiple,
		maxSize = fileMaxSize,
		maxFiles,
		preview,
	} = (fieldProps || {}) as FieldsProps.File;
	let acceptedFiles = accept ? accept.split(',') : undefined;
	const field = useField<FileValue | FileValue[] | undefined>(name);
	const [input, meta, helpers] = field;
	const { setFieldValue } = useFormikContext();

	const { value } = input;
	const { setValue } = helpers;

	const [files, setFiles] = useState<FileWithContents[]>([]);
	const [progress, setProgress] = useState<number>();
	const [success, setSuccess] = useState<string>();
	const [error, setError] = useState<ErrorElement>();
	const [confirm, setConfirm] = useState<string>();

	useEffect(() => {
		const initialFiles = value
			? ((multiple ? value : [value]) as FileValue[]).map((v) => ({ ...v, preview: v.path }))
			: [];
		setFiles(initialFiles);
	}, []);

	useEffect(() => {
		// setTimeout to fix not-updated form state
		setTimeout(() => {
			if (files) {
				const newValue = files.length === 0 ? (multiple ? [] : undefined) : multiple ? files : files[0];
				setValue(newValue);
			}
			// update *UniqueId
			setFieldValue(`${name}UniqueId`, files.length > 0 ? files[0].uniqueId : undefined);
		}, 0);
	}, [multiple, files]);

	const onDrop = useCallback(
		async (acceptedFiles: FileWithPath[]) => {
			setError(undefined);
			if (acceptedFiles.length === 0) return;
			const files = await Promise.all(
				acceptedFiles.map(async (file) => {
					const content = await fileToBlob(file);
					return {
						path: file.path || '',
						fileName: file.name,
						fileLength: file.size,
						type: file.type,
						preview: URL.createObjectURL(file),
						content: content,
					} as FileWithContents;
				})
			);
			if (onSelectFiles) {
				setProgress(0);
				onSelectFiles({
					field,
					files,
					setSuccess: (newFiles: FileWithContents[]) => {
						setFiles(newFiles);
						setSuccess('File caricato correttamente');
						setProgress(undefined);
					},
					setError: (err) => {
						setError(err);
						setProgress(undefined);
					},
					setProgress: (percent) => setProgress(percent),
				});
			}
		},
		[field]
	);

	const onRemove = useCallback(
		(toRemove: FileWithContents) => {
			if (onRemoveFile) {
				setConfirm(undefined);
				onRemoveFile({
					field,
					file: toRemove,
					setSuccess: () => {
						setSuccess('File rimosso');
						setError(undefined);
						setProgress(undefined);
						setFiles(files.filter((file) => toRemove.path !== file.path));
					},
					setError: (err) => {
						setError(err);
						setProgress(undefined);
					},
				});
			}
		},
		[field, files]
	);

	const onDropRejected = useCallback(async (fileRejections: any) => {
		setError(
			fileRejections[0].errors.map((err: { code: any; message: any }) => ({
				code: err.code,
				requestId: '-',
				message: err.message,
			}))
		);
	}, []);

	const dropzoneProps = {
		maxFiles: 1,
		onDrop,
		onDropRejected,
	} as DropzoneOptions;
	if (acceptedFiles) {
		if (typesOfFile) {
			acceptedFiles = acceptedFiles.reduce<string[]>((acc, f: string) => {
				const exp = typesOfFile[f];
				if (exp) {
					return [...acc, ...exp.map((e) => `.${e}`)];
				}
				return [...acc, f];
			}, []);
		}
		dropzoneProps.accept = acceptedFiles;
	}
	if (maxSize) {
		dropzoneProps.maxSize = maxSize * 1024 * 1024;
	}
	if (multiple) {
		dropzoneProps.maxFiles = maxFiles || undefined;
	} else {
		dropzoneProps.multiple = false;
	}
	const { getRootProps, getInputProps } = useDropzone(dropzoneProps);

	if (readonly) {
		return (
			<section key={`field-${name}`} className='Field-file'>
				<FileReadOnly files={files} {...props} />
			</section>
		);
	}

	const propsDownloadFile =
		files && files.length > 0 ? prepareDownloadProps({ field, file: files[0], onDownloadFile }) : undefined;
	return (
		<Fragment>
			{success && <SuccessAlert text={success} onClose={() => setSuccess(undefined)} />}
			{confirm && (
				<Confirm text={confirm} onSuccess={() => onRemove(files[0])} onCancel={() => setConfirm(undefined)} />
			)}
			<section key={`field-${name}`} className='Field-file'>
				{preview && files.length > 0 && (
					<figure className='preview'>
						<img src={files[0].preview} />
					</figure>
				)}
				<div {...getRootProps({ className: 'dropzone input' })}>
					<input type='file' id='formFileLg' {...getInputProps()} className='form-control form-control-lg' />
					<div className='input-group'>
						<span className='form-control'>
							{files && files.length > 0
								? files.length > 1
									? `${files.length} files`
									: files[0].fileName
								: `Nessun file`}
						</span>
						{progress === undefined && (!files || files.length === 0) && (
							<Fragment>
								<span className='input-group-text bi bi-upload'>&nbsp;&nbsp;Seleziona</span>
							</Fragment>
						)}
						{progress === undefined && files && files.length > 0 && (
							<Fragment>
								{files.length > 0 && (
									<Fragment>
										<a {...propsDownloadFile} className='input-group-text bi bi-arrow-bar-down'>
											&nbsp;&nbsp;
											{formatBytes(files.reduce((sum, file) => sum + (file.fileLength || 0), 0))}
										</a>
										<span
											className='input-group-text bi bi-x-lg'
											onClick={(e) => {
												e.stopPropagation();
												setConfirm('Sei sicuro di voler rimuovere questo file?');
											}}
										/>
									</Fragment>
								)}
							</Fragment>
						)}
						{progress !== undefined && (
							<div className='progress'>
								<div style={{ width: `${progress}%` }} />
								<span>{Math.round(progress || 0)}%</span>
							</div>
						)}
					</div>
					<Stack direction='horizontal' gap={3}>
						{acceptedFiles && (
							<div className='info'>
								<OverlayTrigger
									key='bottom'
									placement='bottom'
									overlay={<Tooltip>{acceptedFiles.join(', ')}</Tooltip>}
								>
									<small>File accettati: {acceptedFiles.length} tipi </small>
								</OverlayTrigger>
							</div>
						)}
						{maxSize && (
							<div className='info'>
								<small>Massima dimensione: {maxSize}MB</small>
							</div>
						)}
						{files && files[0] && files[0].hash && <FileHash hash={files[0].hash} />}
					</Stack>
					{error && <ErrorAlert renderAs='text' errors={error} />}
				</div>
				{/* files.length > 1 && (
					<nav className='list'>
						<Table striped bordered hover size='sm'>
							<thead>
								<tr>
									<th>#</th>
									<th>File</th>
									<th>Mime</th>
									<th>Size</th>
								</tr>
							</thead>
							<tbody>
								{files.map((file, i) => (
									<tr key={file.name}>
										<td>{i + 1}</td>
										<td>{file.name}</td>
										<td>{file.type}</td>
										<td>{formatBytes(file.size)}</td>
									</tr>
								))}
							</tbody>
						</Table>
					</nav>
				) */}
			</section>
		</Fragment>
	);
};

export default File;
